import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
	Component,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
	DataSetComponent,
	DataSetFilter,
	DataSetActionButtonComponent,
} from './data-set.component';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { MatButton } from '@angular/material/button';
import { AccessDateFilterPipe } from 'src/app/components/pipes';
@Component({
	selector: 'app-data-set-search',
	templateUrl: './data-set-search.component.html',
	styleUrls: ['./data-set.component.scss', '../../ui/common/main.scss'],
	imports: [
		DataSetActionButtonComponent,
		MatIcon,
		NgClass,
		MatInput,
		MatChipSet,
		MatChip,
		MatButton,
		AccessDateFilterPipe,
	],
})
export class DataSetSearchComponent implements OnInit, OnDestroy {
	@ViewChild('searchInput') searchInput!: ElementRef;
	@ViewChild('filterTemplate') filterTemplate!: TemplateRef<any>;

	@Input() showFilterToggle = true;
	@Input() defaultFilterName!: string;
	@Input() defaultFilterLabel!: string;
	@Input() placeholder!: string;

	private overlayRef?: OverlayRef;

	private filterChangedSubscription?: Subscription;
	private routerSubscription?: Subscription;

	constructor(
		private dataSet: DataSetComponent,
		private overlay: Overlay,
		private viewContainerRef: ViewContainerRef,
		private router: Router
	) {}

	filterItems: DataSetFilter[] = [];

	ngOnInit(): void {
		// Only apply filters if this is the initial load (no data in table yet.)
		// This prevents unnecessary table reloads when the component is recreated due to selection changes,
		// specifically when the user unselects ALL the items they have previously selected.
		const hasExistingData =
			this.dataSet.dataSource &&
			this.dataSet.dataSource.data &&
			this.dataSet.dataSource.data.length > 0;
		if (!hasExistingData) {
			this.applyFilters();
		} else {
			// If there are already active filters, manually sync filterItems without triggering a reload
			this.filterItems = this.dataSet.getCurrentAppliedFilters();
		}

		this.filterChangedSubscription =
			this.dataSet.dataSetFilterChanged.subscribe(
				(updated: DataSetFilter[]) => {
					this.filterItems = updated;
				}
			);

		this.router.events.subscribe((_) => {
			this.closeFilterPanel();
		});
	}

	ngOnDestroy(): void {
		this.filterChangedSubscription?.unsubscribe();
		this.filterChangedSubscription = undefined;

		this.routerSubscription?.unsubscribe();
		this.routerSubscription = undefined;
	}

	get currentlySearching(): boolean {
		return !this.dataSet.anyActiveFilters;
	}

	toggleFilterPanel(): void {
		if (this.overlayRef !== undefined) {
			this.closeFilterPanel();
			return;
		}

		this.dataSet.prepareFiltersForEditing();

		const dimensions = this.dataSet.dataSetContentDimensions;
		const width = dimensions.right - dimensions.left;
		const height = dimensions.bottom - dimensions.top;
		this.overlayRef = this.overlay.create({
			hasBackdrop: true,
			scrollStrategy: this.overlay.scrollStrategies.block(),
			positionStrategy: this.overlay
				.position()
				.flexibleConnectedTo({
					x: dimensions.left,
					y: dimensions.top,
					width,
					height,
				})
				.withPositions([
					{
						originX: 'start',
						originY: 'top',
						overlayX: 'start',
						overlayY: 'top',
					},
				]),
		});

		this.overlayRef.attach(
			new TemplatePortal(this.filterTemplate, this.viewContainerRef)
		);
		this.overlayRef?.updateSize({ width });
	}

	applyFilters(): void {
		this.dataSet.applyFilters();

		this.overlayRef?.dispose();
		this.overlayRef = undefined;
	}

	closeFilterPanel(): void {
		this.dataSet.resetEditedFilters();

		this.overlayRef?.dispose();
		this.overlayRef = undefined;
	}

	removeFilter(filter: DataSetFilter): void {
		this.dataSet.removeFilter(filter.name);
		this.dataSet.resetEditedFilters();
	}

	removeAllFilters(): void {
		this.dataSet.clearFilters();
	}

	getDisplayValue(filter: DataSetFilter): string {
		let value: string | undefined;
		if (filter.dropdownOptions !== undefined) {
			value = filter.dropdownOptions.find(
				(option) => option.value === filter.value
			)?.label;
		}
		return value ?? filter.value;
	}

	getRemoveFilterAriaLabel(filter: DataSetFilter): string {
		const removeText = $localize`:@@dataSetSearch-removeFilter:Remove filter`;
		return `${removeText} ${filter.label}: ${this.getDisplayValue(filter)}`;
	}

	applyDefaultFilter(): void {
		const value = this.searchInput.nativeElement.value;
		this.dataSet.filter(this.defaultFilterName, this.defaultFilterLabel, value);
		this.dataSet.applyFilters();
	}

	get filterOverlayVisible(): boolean {
		return this.overlayRef !== undefined;
	}

	// Below for unit testing only
	setDataSet(dataSet: any) {
		this.dataSet = dataSet;
	}
}
